import { memo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { getDayAndMonthFromUTC, createDataset } from "utils/formatters";
import { RegionChartSingleKneeProps } from "constants/types";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export const RegionChartSingleKnee = memo(
  ({
    examMLResultRightKnee,
    examMLResultLeftKnee,
    title,
  }: RegionChartSingleKneeProps) => {
    if (window.location.search.includes("isFreeForm=true")) {
      return <></>;
    }
    const options = {
      plugins: {
        title: {
          display: false,
          text: "Chart.js Bar Chart - Stacked",
        },
        legend: {
          display: false,
        },
      },
      responsive: true,
      interaction: {
        intersect: false,
      },
      scales: {
        x: {
          stacked: true,
        },
      },
    };
    const plugins = [ChartDataLabels];

    const datasets = () => {
      let datasets = [];
      let dateAndStacksLeftLeg: {
        dayAndMonth: string | undefined;
        stack: number;
      }[] = [];
      let dateAndStacksRightLeg: {
        dayAndMonth: string | undefined;
        stack: number;
      }[] = [];
      let stack = 1;
      const allExamMLResult = [
        ...examMLResultRightKnee,
        ...examMLResultLeftKnee,
      ];
      if (allExamMLResult.length > 0) {
        let allExamMLResultSorted = [...allExamMLResult].sort(
          (a: any, b: any) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
        if (allExamMLResultSorted && allExamMLResultSorted.length > 0) {
          for (let i = 0; i < allExamMLResultSorted.length; i++) {
            let dayAndMonth = getDayAndMonthFromUTC(
              allExamMLResultSorted[i].createdAt
            );
            if (
              allExamMLResultSorted[i].regionId == "624d9d053a7e5392abe5f78e" ||
              allExamMLResultSorted[i].regionId == "626e5b81f613a8eac4cf7300"
            ) {
              //left
              if (
                dateAndStacksLeftLeg.length > 0 &&
                dayAndMonth ==
                  dateAndStacksLeftLeg[dateAndStacksLeftLeg.length - 1]
                    .dayAndMonth
              ) {
                stack = dateAndStacksLeftLeg[
                  dateAndStacksLeftLeg.length - 1
                ].stack += 2;
              } else {
                stack = 1;
              }
              if (allExamMLResultSorted[i].leftMinValue > 0) {
                datasets.push(
                  createDataset(
                    "Left Knee Min Value",
                    dayAndMonth,
                    allExamMLResultSorted[i],
                    "#FFFFFF",
                    stack.toString(),
                    ""
                  )
                );
              } else if (allExamMLResultSorted[i].leftMinValue < 0) {
                datasets.push(
                  createDataset(
                    "Left Knee Min Value",
                    dayAndMonth,
                    allExamMLResultSorted[i],
                    "#C83D57",
                    stack.toString(),
                    ""
                  )
                );
              }
              if (allExamMLResultSorted[i].leftMaxValue > 0) {
                datasets.push(
                  createDataset(
                    "Left Knee Max Value",
                    dayAndMonth,
                    allExamMLResultSorted[i],
                    "#52B788",
                    stack.toString(),
                    "L"
                  )
                );
              } else if (allExamMLResultSorted[i].leftMaxValue < 0) {
                datasets.push(
                  createDataset(
                    "Left Knee Max Value",
                    dayAndMonth,
                    allExamMLResultSorted[i],
                    "#FFFFFF",
                    stack.toString(),
                    "L"
                  )
                );
              }
              dateAndStacksLeftLeg.push({ dayAndMonth, stack });
            } else if (
              allExamMLResultSorted[i].regionId == "626e5b6ff613a8eac4cf72ff" ||
              allExamMLResultSorted[i].regionId == "624d9c5f3a7e5392abe5f78d"
            ) {
              //right

              if (
                dateAndStacksRightLeg.length > 0 &&
                dayAndMonth ==
                  dateAndStacksRightLeg[dateAndStacksRightLeg.length - 1]
                    .dayAndMonth
              ) {
                stack =
                  dateAndStacksRightLeg[dateAndStacksRightLeg.length - 1]
                    .stack + 2;
              } else {
                stack = 2;
              }
              if (allExamMLResultSorted[i].rightMinValue > 0) {
                datasets.push(
                  createDataset(
                    "Right Knee Min Value",
                    dayAndMonth,
                    allExamMLResultSorted[i],
                    "#FFFFFF",
                    stack.toString(),
                    ""
                  )
                );
              } else if (allExamMLResultSorted[i].rightMinValue < 0) {
                datasets.push(
                  createDataset(
                    "Right Knee Min Value",
                    dayAndMonth,
                    allExamMLResultSorted[i],
                    "#C83D57",
                    stack.toString(),
                    ""
                  )
                );
              }
              if (allExamMLResultSorted[i].rightMaxValue > 0) {
                datasets.push(
                  createDataset(
                    "Right Knee Max Value",
                    dayAndMonth,
                    allExamMLResultSorted[i],
                    "#52B788",
                    stack.toString(),
                    "R"
                  )
                );
              } else if (allExamMLResultSorted[i].rightMaxValue < 0) {
                datasets.push(
                  createDataset(
                    "Right Knee Max Value",
                    dayAndMonth,
                    allExamMLResultSorted[i],
                    "#FFFFFF",
                    stack.toString(),
                    "R"
                  )
                );
              }
              dateAndStacksRightLeg.push({ dayAndMonth, stack });
            }
          }
        }
      }
      return datasets;
    };

    const data = {
      datasets: datasets(),
    };
    return (
      <>
        <div className="flex">
          <strong className="text-[18px] mr-[46px] capitalize ">
            {title} Progress Chart
          </strong>
          <div className="h-[10px] w-[10px] bg-[#52B788] rounded-[6px] self-center mr-[4px]"></div>
          Varus
          <div className="h-[10px] w-[10px] bg-[#C83D57] rounded-[6px] self-center mr-[4px] ml-[16px]"></div>
          Valgus
        </div>
        {/* @ts-ignore: Unreachable code error */}
        {data.datasets.length > 0 ? (
          <Bar options={options} plugins={plugins} data={data} />
        ) : (
          <div
            style={{
              width: "100%",
              height: 200,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            No data available
          </div>
        )}
      </>
    );
  }
);
